import React, { useRef, useState } from 'react';

import {
  BarChart3,
  Camera,
  Flag,
  MapPin,
  Pencil,
  Settings,
  Share2,
  Swords,
  UserRound,
} from 'lucide-react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import CourseLogo from '../../components/common/CourseLogo';
import ShareProfileDrawer from '../../components/drawers/ShareProfileDrawer';
import AdminLayout from '../../components/layout/AdminLayout';
import MobilePageBar, {
  isPhoneLayout,
  MobilePageBarButton,
} from '../../components/layout/MobilePageBar';
import AccountSettings from '../../components/member/AccountSettings';
import MemberStats from '../../components/member/MemberStats';
import PlayedCountriesMap from '../../components/member/PlayedCountriesMap';
import ProfileAvatar from '../../components/member/ProfileAvatar';
import {
  DetailRow,
  EditLink,
  PersonalInfoCard,
} from '../../components/member/ProfileDetails';
import ProfileEditDrawer from '../../components/member/ProfileEditDrawer';
import {
  choiceLabel,
  formatHandicapIndex,
  GAMBLE_PREFERENCES,
  languageLabels,
  PACES_OF_PLAY,
  golfTabRoute,
  GolfTabId,
  PROFILE_SETTINGS_ROUTE,
} from '../../constants/GolferProfileConstants';
import {
  useMyCoursesPlayed,
  useMyStats,
} from '../../hooks/useMemberCoursesPlayedApi';
import { useMyHomeCourses } from '../../hooks/useMemberHomeCourseApi';
import {
  useMyProfile,
  useUploadMyProfilePicture,
} from '../../hooks/useMemberProfileApi';
import { MemberProfileDTO, MemberStatsDTO } from '../../services/apiClient';
import { showToast } from '../../utils/showToast';

type TabId = 'about' | 'games' | 'account';

// The Account tab is desktop only. On a phone the same cards are on the Settings page, behind the
// gear in the top bar.
const TABS: {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  desktopOnly?: boolean;
}[] = [
  { id: 'about', label: 'About', icon: <UserRound className="h-4 w-4" /> },
  { id: 'games', label: 'Games', icon: <Swords className="h-4 w-4" /> },
  {
    id: 'account',
    label: 'Account',
    icon: <Settings className="h-4 w-4" />,
    desktopOnly: true,
  },
];

const PICTURE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// The Instagram-style numbers beside the picture (Instagram has posts, followers, following).
// Each one opens the member's golf lists on its own tab. Buddies will take the bucket list's place
// here once they exist, and the bucket list stays a tab on that page.
const COUNTERS: {
  tab: GolfTabId;
  label: [string, string];
  value: (stats: MemberStatsDTO) => number | undefined;
}[] = [
  {
    tab: 'courses',
    label: ['course', 'courses'],
    value: (stats) => stats.coursesPlayed,
  },
  {
    tab: 'countries',
    label: ['country', 'countries'],
    value: (stats) => stats.countriesPlayed,
  },
  {
    tab: 'bucket',
    label: ['bucket list', 'bucket list'],
    value: (stats) => stats.bucketList,
  },
];

const ProfileCounters: React.FC<{
  stats: MemberStatsDTO | null | undefined;
}> = ({ stats }) => (
  <ul className="mt-2 grid max-w-sm grid-cols-3 gap-2">
    {COUNTERS.map((counter) => {
      const value = stats != null ? (counter.value(stats) ?? 0) : null;
      return (
        <li key={counter.tab}>
          <Link
            to={golfTabRoute(counter.tab)}
            className="block rounded-md py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span className="block font-display text-lg font-semibold leading-tight text-ink dark:text-white">
              {value != null ? value.toLocaleString() : '-'}
            </span>
            <span className="block whitespace-nowrap text-xs text-ink-muted sm:text-sm dark:text-white/60">
              {value === 1 ? counter.label[0] : counter.label[1]}
            </span>
          </Link>
        </li>
      );
    })}
  </ul>
);

const yesNo = (value: boolean | undefined): string | null =>
  value == null ? null : value ? 'Yes' : 'No';

// The member's own profile. The order of things follows the AllSquare profile the client pointed
// to (picture, name, "Edit my profile", the short facts, then tabs), but it is built from the same
// Card and Button as every other member page, so it looks like part of the app.
//
// Phone and desktop differ on purpose (the user's call, 2026-10-19). On a phone the profile is the
// member's home, reached from the flag on the map, and it works like Instagram: a back arrow to the
// map, a gear for Settings (where editing lives), no breadcrumbs, no menu, no edit buttons on the
// page itself. On a desktop it stays an admin-style page with the full layout and the Account tab.
//
// The header is what other golfers see when the member shares their profile (Share profile, then
// /shared/profile/{code}, see SharedProfile). It shows one home club, the primary (picked with
// Make primary on the home courses list), and none when the member hid it. The home courses
// themselves are managed on that list, not on this page (the user's call, 2026-10-19).
const Profile: React.FC = () => {
  const { data: profile, isLoading, isError } = useMyProfile();
  const { data: homeCourses } = useMyHomeCourses();
  const { data: coursesPlayed } = useMyCoursesPlayed();
  const { data: stats } = useMyStats();
  const uploadPicture = useUploadMyProfilePicture();
  const navigate = useNavigate();

  // ?tab=account opens a tab directly (the Explore drawer's Membership row does). A phone has no
  // Account tab, so there it goes to Settings instead, which holds the same cards.
  const [searchParams] = useSearchParams();
  const [sendToSettings] = useState(
    () => searchParams.get('tab') === 'account' && isPhoneLayout(),
  );
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const requested = searchParams.get('tab');
    return TABS.some((tab) => tab.id === requested)
      ? (requested as TabId)
      : 'about';
  });
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const breadcrumbs = [{ label: 'My Profile', href: '#' }];

  const handlePicture = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file == null) {
      return;
    }
    if (!PICTURE_TYPES.includes(file.type)) {
      showToast('Choose a JPEG, PNG, WebP or GIF image.', 'error');
      return;
    }
    try {
      await uploadPicture.mutateAsync(file);
      showToast('Profile picture saved.', 'success');
    } catch {
      showToast('Could not save that picture.', 'error');
    }
  };

  const renderBody = (me: MemberProfileDTO): React.ReactNode => {
    // The backend marks exactly one row primary (its fallback is the first by name).
    const primaryClub = (homeCourses ?? []).find(
      (row) => row.isPrimary && row.private !== true,
    );
    const index = formatHandicapIndex(me.handicapIndex);
    const location = [me.stateName, me.countryName]
      .filter((part) => part != null && part.trim().length > 0)
      .join(', ');
    const playedCount = stats?.coursesPlayed;
    const countryCount = stats?.countriesPlayed;

    return (
      <>
        <Card>
          <div className="flex items-center gap-4 sm:items-start sm:gap-5">
            <div className="relative w-fit shrink-0">
              <ProfileAvatar
                pictureUrl={me.profilePictureUrl}
                firstName={me.firstName}
                lastName={me.lastName}
                className="h-20 w-20 sm:h-28 sm:w-28"
                initialsClassName="text-xl sm:text-3xl"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadPicture.isPending}
                aria-label="Change profile picture"
                className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-secondary text-white shadow transition-transform hover:scale-105 disabled:cursor-wait disabled:opacity-60 dark:border-gray-800"
              >
                <Camera className="h-4 w-4" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={PICTURE_TYPES.join(',')}
                className="hidden"
                onChange={(e) => void handlePicture(e)}
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <h1 className="font-display text-base font-semibold text-ink sm:text-xl dark:text-white">
                  {me.firstName} {me.lastName}
                </h1>
                {/* Desktop only. On a phone, editing is in Settings (the gear), and Share profile
                    sits under the bio, as on Instagram. */}
                <div className="hidden gap-2 lg:flex">
                  <Button
                    variant="secondary"
                    onClick={() => setIsShareOpen(true)}
                    icon={Share2}
                  >
                    Share profile
                  </Button>
                  <Button onClick={() => setIsEditOpen(true)} icon={Pencil}>
                    Edit my profile
                  </Button>
                </div>
              </div>

              <ProfileCounters stats={stats} />
            </div>
          </div>

          {/* Under the picture on a phone, under the name from sm up (the picture is 7rem plus
              the 1.25rem gap). */}
          <div className="sm:pl-[8.25rem]">
            {/* The bio. Without one, a prompt to write it: on a phone editing is in Settings. */}
            {me.about ? (
              <p className="mt-4 max-w-measure whitespace-pre-line text-sm text-ink-muted dark:text-white/70">
                {me.about}
              </p>
            ) : (
              <button
                type="button"
                onClick={() =>
                  isPhoneLayout()
                    ? navigate(PROFILE_SETTINGS_ROUTE)
                    : setIsEditOpen(true)
                }
                className="mt-4 text-sm font-medium text-primary-deep underline-offset-2 hover:underline dark:text-primary"
              >
                Add a bio
              </button>
            )}

            {(primaryClub != null || index != null || location.length > 0) && (
              <ul className="mt-4 space-y-2">
                {primaryClub != null && (
                  <li className="flex items-center gap-3">
                    <CourseLogo
                      logoFileId={primaryClub.courseLogoFileId}
                      name={primaryClub.courseName ?? ''}
                      closed={primaryClub.courseClosed}
                      className="h-8 w-8 rounded"
                    />
                    <span className="text-sm font-medium text-ink dark:text-white">
                      {primaryClub.courseName}
                    </span>
                  </li>
                )}
                {index != null && (
                  <li className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center">
                      <BarChart3 className="h-5 w-5 text-ink-muted dark:text-white/60" />
                    </span>
                    <span className="text-sm text-ink dark:text-white">
                      Golf index {index}
                    </span>
                  </li>
                )}
                {location.length > 0 && (
                  <li className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center">
                      <MapPin className="h-5 w-5 text-ink-muted dark:text-white/60" />
                    </span>
                    <span className="text-sm text-ink dark:text-white">
                      Lives in {location}
                    </span>
                  </li>
                )}
              </ul>
            )}

            <div className="mt-4 lg:hidden">
              <Button
                variant="secondary"
                onClick={() => setIsShareOpen(true)}
                icon={Share2}
                className="w-full"
              >
                Share profile
              </Button>
            </div>
          </div>
        </Card>

        {/* The same look as the shared Tab component (deep gold text over a gold underline). */}
        <div
          role="tablist"
          aria-label="Profile sections"
          className="mx-4 flex space-x-1 overflow-x-auto border-b border-primary/30 dark:border-primary/30"
        >
          {TABS.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={`${tab.desktopOnly ? 'hidden lg:flex' : 'flex'} items-center gap-2 whitespace-nowrap rounded-t-md px-4 py-2 text-sm font-semibold transition-colors focus:outline-none ${
                  isActive
                    ? 'border-b-2 border-primary bg-primary/5 text-primary-deep dark:bg-white/5 dark:text-primary'
                    : 'text-ink-muted hover:bg-gray-50 hover:text-ink dark:text-white/60 dark:hover:bg-white/5 dark:hover:text-white'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            );
          })}
        </div>

        <div role="tabpanel">
          {activeTab === 'about' && (
            <>
              <Card
                title="Golfer profile"
                headerActions={
                  <EditLink
                    onClick={() => setIsEditOpen(true)}
                    className="hidden lg:flex"
                  />
                }
              >
                <dl>
                  <DetailRow label="Golf index" value={index} />
                  <DetailRow
                    label="Like to gamble"
                    value={choiceLabel(GAMBLE_PREFERENCES, me.gamblePreference)}
                  />
                  <DetailRow
                    label="Pace of play"
                    value={choiceLabel(PACES_OF_PLAY, me.paceOfPlay)}
                  />
                  <DetailRow
                    label="Languages"
                    value={languageLabels(me.languages).join(', ') || null}
                  />
                  <DetailRow
                    label="Play as a couple"
                    value={yesNo(me.playsAsCouple)}
                  />
                </dl>
              </Card>

              <Card
                title="Golf courses played"
                subtitle={
                  playedCount != null
                    ? `Hey ${me.firstName}, you have played ${playedCount.toLocaleString()} golf ${
                        playedCount === 1 ? 'course' : 'courses'
                      }${
                        countryCount != null && countryCount > 0
                          ? ` in ${countryCount.toLocaleString()} ${countryCount === 1 ? 'country' : 'countries'}`
                          : ''
                      }.`
                    : undefined
                }
              >
                <MemberStats />
                {/* A picture, not a live map: Explore is the live map, and a tap opens it. */}
                <div className="mt-4">
                  <PlayedCountriesMap
                    coursesPlayed={coursesPlayed ?? []}
                    homeCourses={homeCourses ?? []}
                  />
                </div>
              </Card>
            </>
          )}

          {activeTab === 'games' && (
            <Card title="Game requests">
              <div className="flex flex-col items-center py-8 text-center">
                <Flag className="h-10 w-10 text-primary" />
                <p className="mt-3 font-display text-lg font-semibold text-ink dark:text-white">
                  Coming soon
                </p>
                <p className="mt-1 max-w-sm text-sm text-ink-muted dark:text-white/60">
                  Soon you can ask other members for a game, and they can ask
                  you.
                </p>
              </div>
            </Card>
          )}

          {activeTab === 'account' && (
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <PersonalInfoCard
                profile={me}
                onEdit={() => setIsEditOpen(true)}
              />
              <AccountSettings profile={me} />
            </div>
          )}
        </div>

        <ProfileEditDrawer
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          profile={me}
        />
        <ShareProfileDrawer
          isOpen={isShareOpen}
          onClose={() => setIsShareOpen(false)}
        />
      </>
    );
  };

  if (sendToSettings) {
    return <Navigate to={PROFILE_SETTINGS_ROUTE} replace />;
  }

  return (
    <AdminLayout
      activeMenuItem="/member/profile"
      breadcrumbs={breadcrumbs}
      allowedRoles={['User']}
      mobileBar={
        <MobilePageBar
          title="Profile"
          backTo="/explore"
          backLabel="Back to the map"
          action={
            <MobilePageBarButton
              label="Settings"
              icon={Settings}
              onClick={() => navigate(PROFILE_SETTINGS_ROUTE)}
            />
          }
        />
      }
    >
      {isLoading ? (
        <p className="m-4 text-ink-muted">Loading your profile...</p>
      ) : isError || profile == null ? (
        <p role="alert" className="m-4 text-ink-muted">
          Your profile could not be loaded just now.
        </p>
      ) : (
        renderBody(profile)
      )}
    </AdminLayout>
  );
};

export default Profile;
